
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { LegacyPlanSection } from '@/types/billing-types';

export default defineComponent({
  name: 'LegacyPlanHeroCard',

  components: { SpmButton },

  props: {
    legacyPlan: {
      type: Object as PropType<LegacyPlanSection>,
      required: true,
    },
  },

  emits: ['change-plan'],

  setup(props) {
    const { t } = useI18n();

    const isActive = computed(() => props.legacyPlan.status === 'active');

    const eyebrow = computed(() => (isActive.value
      ? t('billing.subscriptionPage.legacyHero.eyebrowCurrent')
      : t('billing.subscriptionPage.legacyHero.eyebrowNone')));

    const title = computed(() => (
      props.legacyPlan.bundleName
      ?? t('billing.subscriptionPage.legacyHero.noOfferTitle')
    ));

    const subtitle = computed(() => (isActive.value
      ? t('billing.subscriptionPage.legacyHero.subtitleActive')
      : t('billing.subscriptionPage.legacyHero.subtitleNone')));

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    // Automation + Newsletter modules, each with its monthly allowance as
    // sub-text. Both are always listed so the shop sees what it could add.
    const modules = computed(() => {
      const { hasAutomation, hasNewsletter, pageViewsCap, newsletterCap } = props.legacyPlan;
      return [
        {
          key: 'automation',
          name: t('billing.subscriptionPage.legacyHero.moduleAutomation'),
          active: hasAutomation,
          detail: hasAutomation && pageViewsCap
            ? t('billing.subscriptionPage.legacyHero.capPageViews', {
              count: formatNumber(pageViewsCap),
            })
            : t('billing.subscriptionPage.legacyHero.moduleNotIncluded'),
        },
        {
          key: 'newsletter',
          name: t('billing.subscriptionPage.legacyHero.moduleNewsletter'),
          active: hasNewsletter,
          detail: hasNewsletter && newsletterCap
            ? t('billing.subscriptionPage.legacyHero.capNewsletter', {
              count: formatNumber(newsletterCap),
            })
            : t('billing.subscriptionPage.legacyHero.moduleNotIncluded'),
        },
      ];
    });

    const formattedRenewal = computed(() => (
      props.legacyPlan.renewalAt
        ? moment(props.legacyPlan.renewalAt).format('DD MMMM YYYY')
        : null
    ));

    const autoRenewLabel = computed(() => (props.legacyPlan.autoRenew
      ? t('billing.subscriptionPage.legacyHero.autoRenewOn')
      : t('billing.subscriptionPage.legacyHero.autoRenewOff')));

    const metricStat = computed(() => {
      const m = props.legacyPlan.metricTotal;
      if (!m) return null;
      return {
        value: formatNumber(m.value),
        label: t('billing.subscriptionPage.legacyHero.metricPagesViewed'),
      };
    });

    return {
      t,
      isActive,
      eyebrow,
      title,
      subtitle,
      modules,
      formattedRenewal,
      autoRenewLabel,
      metricStat,
    };
  },
});
